export const turboMediaProbeSelectors = {
  cspNonceMeta: "meta[name='csp-nonce']",
  vimeoFrame: "iframe[data-vimeo-src]",
  vimeoBlankFrame: "iframe[data-vimeo-src][src='about:blank']",
  wistiaEmbed: ".wistia_embed[data-wistia-video-id]",
  wistiaResetEmbed: ".wistia_embed[data-wistia-video-id][data-wistia-reset='true']",
} as const;

export type TurboMediaProbeState = {
  readonly cspNoncePresent: boolean;
  readonly vimeoFrameCount: number;
  readonly vimeoBlankFrameCount: number;
  readonly wistiaEmbedCount: number;
  readonly wistiaResetEmbedCount: number;
  readonly currentRuntime: "legacy-asset-builder";
  readonly nextRuntime: "esbuild-module";
};

export function turboMediaProbeState(root: ParentNode = document): TurboMediaProbeState {
  return {
    cspNoncePresent: Boolean(root.querySelector(turboMediaProbeSelectors.cspNonceMeta)),
    vimeoFrameCount: root.querySelectorAll(turboMediaProbeSelectors.vimeoFrame).length,
    vimeoBlankFrameCount: root.querySelectorAll(turboMediaProbeSelectors.vimeoBlankFrame).length,
    wistiaEmbedCount: root.querySelectorAll(turboMediaProbeSelectors.wistiaEmbed).length,
    wistiaResetEmbedCount: root.querySelectorAll(turboMediaProbeSelectors.wistiaResetEmbed).length,
    currentRuntime: "legacy-asset-builder",
    nextRuntime: "esbuild-module",
  };
}

export function markTurboMediaProbeReady(documentRef: Document = document): TurboMediaProbeState {
  const state = turboMediaProbeState(documentRef);
  const hasTrackedSurface =
    state.cspNoncePresent ||
    state.vimeoFrameCount > 0 ||
    state.wistiaEmbedCount > 0 ||
    state.wistiaResetEmbedCount > 0;

  documentRef.documentElement.dataset.turboMediaProbe = hasTrackedSurface ? "observed" : "absent";
  documentRef.documentElement.dataset.turboMediaProbeVimeoFrames = String(state.vimeoFrameCount);
  documentRef.documentElement.dataset.turboMediaProbeWistiaEmbeds = String(state.wistiaEmbedCount);

  return state;
}

export function installTurboMediaProbe(documentRef: Document = document): void {
  const markReady = (): void => {
    markTurboMediaProbeReady(documentRef);
  };

  if (documentRef.readyState === "loading") {
    documentRef.addEventListener("DOMContentLoaded", markReady, { once: true });
  } else {
    markReady();
  }

  documentRef.addEventListener("turbo:load", markReady);
  window.addEventListener("pageshow", markReady);
}
